import { useMemo, useState } from 'react';
import { formatDistance, formatDuration } from '@/core/math/scalar';
import { Game } from '@/game/Game';
import { GameSnapshot } from '@/game/Snapshot';
import { BIOMES } from '@/planet/BiomeDef';
import { HUD_SECTIONS, HudVisibility } from './HudSections';
import { useFullscreen, useInstallPrompt } from './pwa';
import { TerrainProfile } from '@/universe/BodyDef';
import { SystemMap } from './SystemMap';
import { useIsTouch } from './useMediaQuery';

export type PanelId = 'map' | 'settings' | 'help' | null;

interface PanelProps {
  game: Game;
  snapshot: GameSnapshot;
  onClose: () => void;
}

/** Settings owns the HUD-visibility state the overlay holds, so it takes it. */
interface SettingsPanelProps extends PanelProps {
  visibility: HudVisibility;
  onVisibilityChange: (next: HudVisibility) => void;
  hudHidden: boolean;
  onHudHiddenChange: (hidden: boolean) => void;
}

function Modal({
  title,
  onClose,
  children,
  tabs,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  tabs?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="modal">
      <div className="modal-header">
        <h2>{title}</h2>
        <button onClick={onClose}>Close</button>
      </div>
      {tabs && <div className="tabs">{tabs}</div>}
      <div className="modal-body">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// System map
// ---------------------------------------------------------------------------

export function MapPanel({ game, snapshot, onClose }: PanelProps): JSX.Element {
  const [selected, setSelected] = useState<string | null>(snapshot.navigation.referenceBodyId);
  const [focus, setFocus] = useState<string | null>(null);
  const [, force] = useState(0);

  const detail = useMemo(() => {
    if (!selected) return null;
    const b = game.system.find(selected);
    if (!b) return null;
    return {
      id: b.id,
      name: b.name,
      kind: b.def.kind,
      parent: b.parent?.name ?? '-',
      radius: b.radius,
      gravity: b.surfaceGravity,
      distanceFromStar: game.system.positionOf(b, game.time).length(),
      dayLength: b.dayLength,
      period: b.orbitalPeriod,
      explorable: b.explorable,
      description: b.def.description,
      moons: game.system.bodies.filter((m) => m.parent?.id === b.id).length,
      visited: snapshot.player.visitedBodies.includes(b.id),
    };
  }, [game, selected, snapshot.player.visitedBodies]);

  const freeFlight = snapshot.player.freeFlight !== null;

  return (
    <Modal
      title={focus ? `${game.system.find(focus)?.name ?? ''} System` : 'Cyrene System'}
      onClose={onClose}
      tabs={
        <>
          <button className={focus === null ? 'active' : ''} onClick={() => setFocus(null)}>
            Star system
          </button>
          {detail && detail.moons > 0 && (
            <button className={focus === detail.id ? 'active' : ''} onClick={() => setFocus(detail.id)}>
              {detail.name} moons
            </button>
          )}
        </>
      }
    >
      {/* Map beside its detail card on a wide screen, stacked below ~720px —
          see `.split-map` in styles.css. Class rather than an inline style
          because an inline `grid-template-columns` cannot be overridden by a
          media query at all, which is what pinned a 300px sidebar next to a
          320px map on a phone. */}
      <div className="split-map">
        <SystemMap game={game} selected={selected} onSelect={setSelected} focus={focus} />

        <div>
          {detail ? (
            <div className="card">
              <h3>{detail.name}</h3>
              <p>{detail.description}</p>
              <div className="row">
                <span className="label">Type</span>
                <span className="value">{detail.kind}</span>
              </div>
              <div className="row">
                <span className="label">Distance from Cyrene</span>
                <span className="value">{formatDistance(detail.distanceFromStar)}</span>
              </div>
              <div className="row">
                <span className="label">Radius</span>
                <span className="value">{formatDistance(detail.radius)}</span>
              </div>
              <div className="row">
                <span className="label">Surface gravity</span>
                <span className="value">{detail.gravity.toFixed(2)} m/s2</span>
              </div>
              <div className="row">
                <span className="label">Day length</span>
                <span className="value">{formatDuration(detail.dayLength)}</span>
              </div>
              <div className="row">
                <span className="label">Orbital period</span>
                <span className="value">
                  {Number.isFinite(detail.period) ? formatDuration(detail.period) : '--'}
                </span>
              </div>
              <div className="row">
                <span className="label">Parent</span>
                <span className="value">{detail.parent}</span>
              </div>
              <div className="row">
                <span className="label">Landable</span>
                <span className={`value ${detail.explorable ? 'good' : 'warn'}`}>
                  {detail.explorable ? 'yes' : 'no'}
                </span>
              </div>
              <div className="button-row" style={{ marginTop: 12 }}>
                <button onClick={() => game.setTarget(detail.id)}>Set target</button>
                {detail.moons > 0 && (
                  <button onClick={() => setFocus(focus === detail.id ? null : detail.id)}>
                    {focus === detail.id ? 'Back out' : `Moons (${detail.moons})`}
                  </button>
                )}
              </div>

              <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
                <div className="row">
                  <span className="label">Free flight</span>
                  <span className={`value ${freeFlight ? 'good' : ''}`}>{freeFlight ? 'on' : 'off (F8)'}</span>
                </div>
                <button
                  className="small"
                  style={{ marginTop: 6 }}
                  onClick={() => {
                    game.fastTravelTo(detail.id);
                    force((n) => n + 1);
                  }}
                >
                  Debug jump to orbit
                </button>
                <p className="hint">
                  Places the vessel in a circular orbit of {detail.name}. A debug shortcut, not a manoeuvre.
                </p>
              </div>
            </div>
          ) : (
            <div className="empty">Select a body.</div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/** Range, in the same order as the `TerrainProfile` interface. */
const TERRAIN_TUNING_FIELDS: { key: keyof TerrainProfile; label: string; min: number; max: number; step: number; decimals: number }[] = [
  { key: 'maxElevation', label: 'Max elevation (m)', min: 500, max: 30000, step: 100, decimals: 0 },
  { key: 'seaLevelFraction', label: 'Sea level fraction', min: -1, max: 1, step: 0.01, decimals: 2 },
  { key: 'oceanCoverage', label: 'Ocean coverage', min: 0, max: 1, step: 0.01, decimals: 2 },
  { key: 'continentScale', label: 'Continent scale (m)', min: 5e4, max: 3e6, step: 1e4, decimals: 0 },
  { key: 'plateCount', label: 'Plate count', min: 0, max: 30, step: 1, decimals: 0 },
  { key: 'ridgeStrength', label: 'Ridge strength', min: 0, max: 1, step: 0.01, decimals: 2 },
  { key: 'mountainStrength', label: 'Mountain strength', min: 0, max: 1.5, step: 0.01, decimals: 2 },
  { key: 'mountainScale', label: 'Mountain scale (m)', min: 1e4, max: 1e6, step: 1e4, decimals: 0 },
  { key: 'warpStrength', label: 'Warp strength', min: 0, max: 1, step: 0.01, decimals: 2 },
  { key: 'detailStrength', label: 'Detail strength', min: 0, max: 1, step: 0.01, decimals: 2 },
  { key: 'detailScale', label: 'Detail scale (m)', min: 10, max: 5000, step: 10, decimals: 0 },
  { key: 'erosion', label: 'Erosion', min: 0, max: 1, step: 0.01, decimals: 2 },
  { key: 'craterDensity', label: 'Crater density', min: 0, max: 1, step: 0.01, decimals: 2 },
  { key: 'craterMaxRadius', label: 'Crater max radius (m)', min: 1000, max: 200000, step: 1000, decimals: 0 },
  { key: 'duneStrength', label: 'Dune strength (m)', min: 0, max: 500, step: 5, decimals: 0 },
  { key: 'duneScale', label: 'Dune scale (m)', min: 100, max: 10000, step: 50, decimals: 0 },
  { key: 'fissureStrength', label: 'Fissure strength', min: 0, max: 1, step: 0.01, decimals: 2 },
  { key: 'roughness', label: 'Roughness', min: 0, max: 2, step: 0.01, decimals: 2 },
];

/**
 * Live-tunes the current body's terrain profile. Every drag regenerates the
 * loaded chunks in place (see `PlanetSurface.updateProfile`) — nothing here
 * is saved, it edits the running generator's parameters, not `planets.ts`.
 * "Copy" formats the current values as a paste-ready `terrain: { ... }`
 * block for whoever is happy with what they see and wants to keep it.
 *
 * Keyed by `bodyId` in the parent so switching planets remounts this with a
 * fresh read of the new body's profile, rather than carrying the previous
 * body's slider positions over onto it.
 */
function TerrainTuningCard({ game, bodyId }: { game: Game; bodyId: string }): JSX.Element {
  const initial = game.getTerrainProfile(bodyId);
  const [values, setValues] = useState<TerrainProfile | null>(initial ? { ...initial } : null);
  const [copied, setCopied] = useState(false);

  if (!values) {
    return (
      <div className="card">
        <h3>Terrain tuning</h3>
        <p className="hint">No surface loaded here yet.</p>
      </div>
    );
  }

  const commit = (key: keyof TerrainProfile, value: number): void => {
    game.updateTerrainProfile(bodyId, { [key]: value } as Partial<TerrainProfile>);
  };

  return (
    <div className="card">
      <h3>Terrain tuning — {bodyId}</h3>
      <p className="hint">
        Edits the live generator and regenerates every loaded chunk in place. Nothing here is saved — copy the values
        below into `planets.ts` once they look right.
      </p>
      {TERRAIN_TUNING_FIELDS.map(({ key, label, min, max, step, decimals }) => (
        <div key={key} className="row" style={{ alignItems: 'center' }}>
          <span className="label" style={{ flex: '0 0 150px' }}>{label}</span>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={values[key]}
            onChange={(e) => setValues({ ...values, [key]: Number(e.target.value) })}
            onMouseUp={() => commit(key, values[key])}
            onTouchEnd={() => commit(key, values[key])}
            onKeyUp={() => commit(key, values[key])}
            style={{ flex: '1 1 auto', margin: '0 8px' }}
          />
          <span className="value" style={{ flex: '0 0 70px', textAlign: 'right' }}>
            {values[key].toFixed(decimals)}
          </span>
        </div>
      ))}
      <button
        className="small"
        style={{ marginTop: 8 }}
        onClick={() => {
          const lines = TERRAIN_TUNING_FIELDS.map(({ key }) => `    ${key}: ${values[key]},`);
          navigator.clipboard.writeText(`terrain: {\n${lines.join('\n')}\n},`).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? 'Copied!' : 'Copy as terrain: { ... }'}
      </button>
    </div>
  );
}

/**
 * Settings.
 *
 * One surface for everything the player can change about the session: which
 * readouts are on screen, the display controls that used to live in the ☰
 * popover, graphics, and the layer switches. Everything that is really an
 * instrument rather than a setting — frame timings, coordinate-frame
 * internals, terrain tuning, the unrestricted-warp debug flag — is still
 * here, but folded into the collapsed "Advanced" block at the bottom so it is
 * not the first thing a player meets.
 */
export function SettingsPanel({
  game,
  snapshot,
  onClose,
  visibility,
  onVisibilityChange,
  hudHidden,
  onHudHiddenChange,
}: SettingsPanelProps): JSX.Element {
  const d = snapshot.diagnostics;
  const p = snapshot.performance;
  const [wireframe, setWireframe] = useState(false);
  const [unrestricted, setUnrestricted] = useState(game.warp.debugUnrestricted);
  const [layers, setLayers] = useState({ ...game.debugLayers });
  const fullscreen = useFullscreen();
  const install = useInstallPrompt();

  const shownCount = HUD_SECTIONS.filter((s) => visibility[s.id]).length;

  return (
    <Modal title="Settings" onClose={onClose}>
      <div className="grid-2">
        <div className="card">
          <h3>
            Readouts{' '}
            <span className="tag">
              {shownCount}/{HUD_SECTIONS.length}
            </span>
          </h3>
          {HUD_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`menu-row${visibility[section.id] && !hudHidden ? ' active' : ''}`}
              onClick={() => onVisibilityChange({ ...visibility, [section.id]: !visibility[section.id] })}
              disabled={hudHidden}
              title={section.hint}
            >
              <span className="menu-check">{visibility[section.id] ? '●' : '○'}</span>
              <span className="menu-label">{section.label}</span>
            </button>
          ))}
        </div>

        <div className="card">
          <h3>Display</h3>
          <button
            type="button"
            className={`menu-row${hudHidden ? ' active' : ''}`}
            onClick={() => onHudHiddenChange(!hudHidden)}
          >
            <span className="menu-check">{hudHidden ? '●' : '○'}</span>
            <span className="menu-label">{hudHidden ? 'HUD hidden' : 'Hide all HUD'}</span>
          </button>
          {fullscreen.supported && (
            <button
              type="button"
              className={`menu-row${fullscreen.active ? ' active' : ''}`}
              onClick={fullscreen.toggle}
            >
              <span className="menu-check">{fullscreen.active ? '●' : '○'}</span>
              <span className="menu-label">Fullscreen</span>
            </button>
          )}
          {install.available && (
            <button type="button" className="menu-row" onClick={install.promptInstall}>
              <span className="menu-check">↓</span>
              <span className="menu-label">Install app</span>
            </button>
          )}
          {install.installed && <div className="menu-note">Running as an installed app</div>}
        </div>

        <div className="card">
          <h3>Graphics</h3>
          <div className="button-row">
            {(['low', 'medium', 'high', 'ultra'] as const).map((level) => (
              <button
                key={level}
                className={game.quality.level === level ? 'active' : ''}
                onClick={() => game.setQuality(level)}
              >
                {level}
              </button>
            ))}
          </div>
          <p style={{ marginTop: 8 }}>
            Every simulation system runs identically at all quality levels. Only visual cost scales.
          </p>

          <div className="row" style={{ marginTop: 10 }}>
            <span className="label">Resolution</span>
          </div>
          <div className="button-row">
            {[0.5, 0.75, 1, 1.25, 1.5, 2].map((scale) => (
              <button
                key={scale}
                className={game.renderScale === scale ? 'active' : ''}
                onClick={() => game.setRenderScale(scale)}
              >
                {Math.round(scale * 100)}%
              </button>
            ))}
          </div>

          <div className="button-row" style={{ marginTop: 10 }}>
            <button
              className={game.terrainSelfShadowEnabled ? 'active' : ''}
              disabled={game.quality.shadowMapSize === 0}
              onClick={() => game.setTerrainSelfShadow(!game.terrainSelfShadowEnabled)}
            >
              Terrain shadows
            </button>
            <button
              className={game.eclipseShadowsEnabled ? 'active' : ''}
              onClick={() => game.setEclipseShadows(!game.eclipseShadowsEnabled)}
            >
              Eclipse shadows
            </button>
            <button
              className={game.vegetationEnabled ? 'active' : ''}
              disabled={game.quality.scatterBudget === 0}
              onClick={() => game.setVegetationEnabled(!game.vegetationEnabled)}
            >
              Vegetation
            </button>
          </div>
          {game.quality.scatterBudget === 0 && (
            <p style={{ marginTop: 8 }}>Vegetation requires Medium quality or higher.</p>
          )}
          {game.quality.shadowMapSize === 0 && (
            <p style={{ marginTop: 8 }}>Terrain shadows require Medium quality or higher.</p>
          )}
        </div>

        <div className="card">
          <h3>Layers</h3>
          <p className="hint">
            Take one away and see what changes. Several problems this session were only separable this way — whether the
            atmosphere shell was darkening the terrain had no answer from a screenshot.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
            {(
              [
                ['surface', 'Terrain'],
                ['atmosphere', 'Atmosphere'],
                ['sky', 'Sky'],
                ['clouds', 'Clouds'],
                ['ocean', 'Ocean'],
                ['vegetation', 'Vegetation'],
                ['stars', 'Stars'],
                ['galaxy', 'Galaxy'],
                ['sunGlare', 'Sun glare'],
                ['distantBodies', 'Distant bodies'],
                ['vessel', 'Vessel'],
                ['trajectory', 'Trajectory'],
                ['targetMarker', 'Target marker'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                className="small"
                style={{ opacity: layers[key] ? 1 : 0.45 }}
                onClick={() => {
                  const next = { ...layers, [key]: !layers[key] };
                  setLayers(next);
                  game.debugLayers[key] = next[key];
                }}
              >
                {layers[key] ? label : `${label} off`}
              </button>
            ))}
          </div>
          <button
            className="small"
            style={{ marginTop: 8 }}
            onClick={() => {
              const next = !wireframe;
              setWireframe(next);
              // Reach through to whichever terrain and ocean renderers are active.
              const bodyId = snapshot.navigation.referenceBodyId;
              const gameAny = game as unknown as {
                terrainRenderers: Map<string, { setWireframe(b: boolean): void }>;
                oceans: Map<string, { setWireframe(b: boolean): void }>;
              };
              gameAny.terrainRenderers?.get(bodyId)?.setWireframe(next);
              gameAny.oceans?.get(bodyId)?.setWireframe(next);
            }}
          >
            {wireframe ? 'Solid terrain' : 'Wireframe terrain'}
          </button>
        </div>
      </div>

      <details className="advanced">
        <summary>Advanced / debug</summary>
        <div className="grid-2" style={{ marginTop: 10 }}>
          <div className="card">
            <h3>Rendering</h3>
            <div className="row"><span className="label">FPS</span><span className="value">{p.fps.toFixed(1)}</span></div>
            <div className="row"><span className="label">Frame time</span><span className="value">{p.frameMs.toFixed(2)} ms</span></div>
            <div className="row"><span className="label">Physics</span><span className="value">{p.physicsMs.toFixed(2)} ms</span></div>
            <div className="row"><span className="label">Render</span><span className="value">{p.renderMs.toFixed(2)} ms</span></div>
            <div className="row"><span className="label">Draw calls</span><span className="value">{p.drawCalls}</span></div>
            <div className="row"><span className="label">Triangles</span><span className="value">{p.triangles.toLocaleString()}</span></div>
            <div className="row"><span className="label">Camera near</span><span className="value">{d.cameraNear.toFixed(3)} m</span></div>
            <div className="row"><span className="label">Camera far</span><span className="value">{d.cameraFar.toExponential(2)} m</span></div>
            <div className="row"><span className="label">Near/far ratio</span><span className="value">{d.nearFarRatio.toExponential(2)}</span></div>
            <div className="row"><span className="label">GPU estimate</span><span className="value">{p.estimatedGpuMB.toFixed(1)} MB</span></div>
          </div>

          <div className="card">
            <h3>Coordinates and frames</h3>
            <div className="row"><span className="label">Active frame</span><span className="value">{d.frameId}</span></div>
            <div className="row"><span className="label">Origin offset</span><span className="value">{formatDistance(d.originOffset)}</span></div>
            <div className="row"><span className="label">Origin shifts</span><span className="value">{d.originShifts}</span></div>
            <div className="row"><span className="label">Distance from origin</span><span className="value">{formatDistance(d.distanceFromOrigin)}</span></div>
            <div className="row"><span className="label">Physics timestep</span><span className="value">{(d.physicsTimestep * 1000).toFixed(2)} ms</span></div>
            <div className="row"><span className="label">Dropped steps</span><span className="value">{p.droppedSteps}</span></div>
          </div>

          <div className="card">
            <h3>Terrain streaming</h3>
            <div className="row"><span className="label">Resident chunks</span><span className="value">{p.chunksResident}</span></div>
            <div className="row"><span className="label">Visible chunks</span><span className="value">{p.chunksVisible}</span></div>
            <div className="row"><span className="label">Pending</span><span className="value">{p.chunksPending}</span></div>
            <div className="row"><span className="label">Worker queue</span><span className="value">{p.workerQueue}</span></div>
            <div className="row"><span className="label">Average build</span><span className="value">{p.workerAverageMs.toFixed(1)} ms</span></div>
            <div className="row"><span className="label">Max LOD level</span><span className="value">{d.maxLodLevel}</span></div>
            <div className="row"><span className="label">Height cache</span><span className="value">{d.heightFieldCache}</span></div>
            <div style={{ marginTop: 8 }}>
              {d.chunksByLevel.map(([level, count]) => (
                <div key={level} className="row">
                  <span className="label">LOD {level}</span>
                  <span className="value">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <TerrainTuningCard key={snapshot.navigation.referenceBodyId} game={game} bodyId={snapshot.navigation.referenceBodyId} />

          <div className="card">
            <h3>Time warp</h3>
            <div className="row">
              <span className="label">Rate</span>
              <span className="value">{snapshot.warp.rate}x</span>
            </div>
            <div className="row">
              <span className="label">Limit</span>
              <span className="value">{unrestricted ? 'none (debug)' : `${snapshot.warp.maxRate}x`}</span>
            </div>
            <button
              className="small"
              style={{ marginTop: 8 }}
              onClick={() => {
                const next = !unrestricted;
                setUnrestricted(next);
                game.warp.debugUnrestricted = next;
              }}
            >
              {unrestricted ? 'Restore warp limits' : 'Unrestricted warp'}
            </button>
            <p className="hint" style={{ marginTop: 8 }}>
              Lifts the situational rate limits, including the 10x cap that applies while landed. A parked vessel is
              carried with the rotating surface rather than propagated, so it stays put — but nothing that needs physics
              can happen while this is on. Useful for watching a day pass on a slow world: Vesper&apos;s is 84 hours,
              Ashkar&apos;s 241.
            </p>
          </div>

          <div className="card">
            <h3>Gravity sources</h3>
            {d.gravitySources.map((g) => (
              <div key={g.name} className="row">
                <span className="label">{g.name}</span>
                <span className="value">
                  {g.magnitude.toExponential(3)} m/s2 @ {formatDistance(g.distance)}
                </span>
              </div>
            ))}
            <p style={{ marginTop: 8 }}>
              Gravity is summed from every body every step rather than switched at a sphere-of-influence boundary, so the
              acceleration field is continuous everywhere.
            </p>
          </div>

          <div className="card">
            <h3>Biome table</h3>
            <div className="scroll-list" style={{ maxHeight: 200 }}>
              {BIOMES.map((b) => (
                <div key={b.id} className="row">
                  <span className="label">{b.name}</span>
                  <span className="value">
                    {b.temperature[0]}-{b.temperature[1]} K
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </details>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------

export function HelpPanel({ onClose }: { onClose: () => void }): JSX.Element {
  const touch = useIsTouch();
  const groups: { title: string; keys: [string, string][] }[] = [
    // First, and only on a touch device: on a phone this is the whole control
    // scheme, and every key listed below it is unreachable.
    ...(touch
      ? [
          {
            title: 'Touch',
            keys: [
              ['Drag', 'Look and steer — the same virtual stick the mouse uses'],
              ['Stick', 'Translate; push forward for thrust, part-way for part power'],
              ['▲ ▼', 'RCS up and down'],
              ['↺ ↻', 'Roll'],
              ['Slider', 'Cruise throttle'],
              ['MORE', 'Engines, holds, macros, warp and camera'],
              ['⚙', 'Settings: readouts, display, graphics, debug'],
            ] as [string, string][],
          },
        ]
      : []),
    {
      title: 'Flight',
      keys: [
        ['W / S', 'Pitch'],
        ['A / D', 'Yaw'],
        ['Q / E', 'Roll'],
        ['Shift / Ctrl', 'Throttle up / down'],
        ['Z / X', 'Full throttle / cut'],
        ['1 / 2 / 3', 'Arm main / atmospheric / landing engines'],
        ['F', 'Toggle field drive'],
        ['R', 'Toggle rotation damping'],
        ['G', 'Hover assist'],
        ['H', 'Altitude hold'],
        ['Alt (hold)', 'Free look — pans the view without turning the ship'],
      ],
    },
    {
      title: 'Navigation',
      keys: [
        ['[ / ]', 'Align prograde / retrograde'],
        ['Numpad 7 / 9', 'Align radial in / out'],
        ['Numpad 4', 'Align normal'],
        ['Numpad 6', 'Align surface up'],
        ['T', 'Align to target'],
        [', / .', 'Time warp down / up'],
        ['/', 'Reset time warp'],
      ],
    },
    {
      title: 'Macros',
      keys: [
        ['F1', 'Launch to orbit'],
        ['F2', 'Powered landing'],
        ['F3', 'Circularise'],
        ['F4', 'Docking approach'],
        ['F6', 'Transfer to target'],
        ['Backspace', 'Abort macro'],
      ],
    },
    {
      title: 'On foot and interface',
      keys: [
        ['B', 'Board / disembark'],
        ['Space', 'Jump'],
        ['V', 'Toggle cockpit / chase camera'],
        ['M', 'System map'],
        ['F9', 'Settings'],
        ['Esc', 'Pause'],
      ],
    },
  ];

  return (
    <Modal title="Controls" onClose={onClose}>
      <div className="grid-2">
        {groups.map((g) => (
          <div key={g.title} className="card">
            <h3>{g.title}</h3>
            {g.keys.map(([key, action]) => (
              <div key={key} className="row">
                <span className="label">
                  <span className="help-key">{key}</span>
                </span>
                <span className="value" style={{ fontSize: 11 }}>
                  {action}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  );
}
